import { css } from "@emotion/react";
import type {
    GetStaticPropsContext,
    InferGetStaticPropsType,
    NextPage,
    PreviewData,
} from "next";
import { ParsedUrlQuery } from "querystring";

import { Divider } from "@/ui/atoms/divider";
import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Text } from "@/ui/atoms/text";
import { RichText } from "@/ui/molecules/rich_text";
import { PrivacyHero } from "@/ui/wrappers/privacy_hero";

import { generatePageLevelProps } from "@/util/data_util";
import { getGlobals, getPrivacyPage } from "@/util/strapi_util";

export const getStaticProps = async ({
    preview: previewMode = false,
}: GetStaticPropsContext<ParsedUrlQuery, PreviewData>) => {
    const slug = "privacy";

    const globalsData = await getGlobals(previewMode);
    const pageData = await getPrivacyPage(previewMode);

    return {
        props: {
            ...generatePageLevelProps({ path: `/${slug}`, previewMode, slug }),
            globalsData,
            pageData,
        },
    };
};

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Page: NextPage<PageProps> = (props) => {
    /**
     * Styles
     */
    const privacyIntroductionStyles = css({
        alignItems: "flexStart",
        display: "flex",
        flexDirection: "column",
    });

    /**
     * Elements
     */
    return (
        <>
            <PrivacyHero {...props.pageData} />

            <Divider
                marginBottom="LongformDividerMargin"
                marginTop="LongformDividerMargin"
            />

            <GridContainer>
                <GridColumn
                    className={privacyIntroductionStyles}
                    columnSpan={{
                        small: 6,
                        medium: 8,
                    }}
                    columnStart={{
                        small: 1,
                        medium: 3,
                    }}
                    marginTop="Default"
                >
                    <Text
                        fontSize="LongformHeadlineLarge"
                        marginBottom="LongformHeadlineSmall"
                        marginTop="Default"
                        tag="h2"
                        textAlign="center"
                    >
                        {props.pageData.Intro_Headline}
                    </Text>
                </GridColumn>

                {props.pageData.Intro_Description && (
                    <GridColumn
                        columnSpan={6}
                        columnStart={{
                            small: 1,
                            medium: 4,
                        }}
                    >
                        <RichText config="Longform">
                            {props.pageData.Intro_Description}
                        </RichText>
                    </GridColumn>
                )}
            </GridContainer>

            <Divider
                marginBottom="LongformDividerMargin"
                marginTop="LongformDividerMargin"
            />

            <GridContainer>
                <GridColumn
                    className={css({
                        alignItems: "flexStart",
                        display: "flex",
                        flexDirection: "column",
                    })}
                    columnSpan={6}
                    columnStart={{
                        small: 1,
                        medium: 4,
                    }}
                    marginBottom="LongformPageContainer"
                >
                    <RichText config="Longform">
                        {props.pageData.Article_Text}
                    </RichText>
                </GridColumn>
            </GridContainer>
        </>
    );
};

export default Page;
